/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — print/export sheet geometry. Pure functions ported
   from the old `OrgChartPrint.tsx` (paper presets, px/in sizing, the
   viewport math that fits the FULL node/edge set to the sheet). Kept
   framework-free so it's trivially testable and reusable by both the
   PDF and print code paths.
   ═══════════════════════════════════════════════════════════════ */

import { getNodesBounds, getViewportForBounds } from '@xyflow/react';
import type { Node, Viewport } from '@xyflow/react';

// ─── Paper presets — plotter sizes first-class per Josh (office has an
//     11x17 printer AND a plotter). Dimensions in inches. ───
export type PresetKey = 'letter' | 'tabloid' | 'archD' | 'archE' | 'custom';
export const PRESETS: Record<
  Exclude<PresetKey, 'custom'>,
  { label: string; w: number; h: number }
> = {
  letter: { label: 'Letter (8.5 × 11")', w: 8.5, h: 11 },
  tabloid: { label: 'Tabloid / Ledger (11 × 17")', w: 11, h: 17 },
  archD: { label: 'Arch D (24 × 36")', w: 24, h: 36 },
  archE: { label: 'Arch E (36 × 48")', w: 36, h: 48 },
};

// Target long-edge pixel size for the rasterized sheet — high enough for
// legible print quality on Letter, bounded so an Arch E capture doesn't ask
// the rasterizer to paint a multi-thousand-pixel canvas.
export const TARGET_LONG_EDGE_PX = 2600;
export const MAX_PX_PER_IN = 300;
export const HEADER_H_PX = 130;
export const LEGEND_H_PX = 70;
export const MARGIN_PX = 40;

export function todayLabel(): string {
  return new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Physical paper size in inches, already oriented per the chosen radio. */
export function paperInches(
  preset: PresetKey,
  customW: number,
  customH: number,
  orientation: 'landscape' | 'portrait'
): { w: number; h: number } {
  const base = preset === 'custom' ? { w: customW || 1, h: customH || 1 } : PRESETS[preset];
  const [longSide, shortSide] = [Math.max(base.w, base.h), Math.min(base.w, base.h)];
  return orientation === 'landscape'
    ? { w: longSide, h: shortSide }
    : { w: shortSide, h: longSide };
}

export interface SheetLayout {
  widthPx: number;
  heightPx: number;
  viewport: Viewport;
  contentW: number;
  contentH: number;
}

/** Computes the pixel sheet size + the React-Flow viewport that fits every
 *  drawn node (not just what's on screen) into the content area, exactly
 *  mirroring the old dialog's layout math. */
export function computeSheetLayout(
  wIn: number,
  hIn: number,
  drawNodes: Node[],
  scaleMode: 'fit' | 'actual',
  showLegend: boolean
): SheetLayout {
  const longEdgeIn = Math.max(wIn, hIn);
  const pxPerIn = Math.min(MAX_PX_PER_IN, TARGET_LONG_EDGE_PX / longEdgeIn);
  const widthPx = Math.round(wIn * pxPerIn);
  const heightPx = Math.round(hIn * pxPerIn);

  const contentW = widthPx - MARGIN_PX * 2;
  const contentH = heightPx - HEADER_H_PX - MARGIN_PX * 2 - (showLegend ? LEGEND_H_PX : 0);

  let viewport: Viewport;
  if (drawNodes.length === 0) {
    viewport = { x: 0, y: 0, zoom: 1 };
  } else if (scaleMode === 'actual') {
    const bounds = getNodesBounds(drawNodes);
    viewport = { x: -bounds.x, y: -bounds.y, zoom: 1 };
  } else {
    const bounds = getNodesBounds(drawNodes);
    viewport = getViewportForBounds(bounds, contentW, contentH, 0.02, 4, 0.04);
  }

  return { widthPx, heightPx, viewport, contentW, contentH };
}
